import { useState, useEffect, useMemo } from 'react';
import NewIdeaModal from './NewIdeaModal';
import { useUI } from '../context/UIContext';
import { useAuth } from '../context/AuthContext';
import { useIdeas } from '@/api/queries';
import { SkeletonKanban } from './common/skeletons/SkeletonKanban';
import { useQueryClient } from '@tanstack/react-query';
import StandardHeader from './layout/StandardHeader';
import { AIActionDropdown } from './common/AIActionsComponent';
import { Plus, Download, Upload } from 'lucide-react';
import TimelineView, { TimelineItem } from './common/TimelineView';
import apiClient from '../api/client';

import IdeaStats from './ideias/IdeaStats';
import ProductActionBar from './produtos/ProductActionBar';

import BoardToolbar from './common/management/BoardToolbar';
import BoardKanban, { KanbanColumn, KanbanItem } from './common/management/BoardKanban';
import BoardList from './common/management/BoardList';
import { useNavigation } from '../context/NavigationContext';

const IDEIA_COLUMNS: KanbanColumn[] = [
  { id: 'capturadas', label: '💡 Capturadas', badge: 'bg-neutral-50 text-neutral-500 border border-neutral-200/50 font-bold' },
  { id: 'avaliacao', label: '🔍 Em análise', badge: 'bg-amber-50 text-amber-800 border border-amber-200/30 font-bold' },
  { id: 'pesquisa', label: '📋 Refinamento', badge: 'bg-indigo-50 text-indigo-800 border border-indigo-200/50 font-bold' },
  { id: 'mvp', label: '💰 Business Case', badge: 'bg-purple-50 text-purple-800 border border-purple-200/50 font-bold' },
  { id: 'aprovadas', label: '✅ Aprovadas', badge: 'bg-sky-50 text-sky-800 border border-sky-200/50 font-bold' },
  { id: 'desenvolvimento', label: '🚀 Em Desenvolvimento', badge: 'bg-blue-50 text-blue-800 border border-blue-200/50 font-bold' },
  { id: 'entregues', label: '🎉 Entregues', badge: 'bg-emerald-50 text-emerald-800 border border-emerald-200/30 font-bold' },
  { id: 'arquivadas', label: '📦 Arquivadas', badge: 'bg-rose-50 text-rose-800 border border-rose-200/30 font-bold' }
];

export default function IdeiasView() {
  const { globalFilters, setGlobalFilters } = useNavigation();
  const { setCurrentView } = useUI();
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState<any>(null);

  const [ideas, setIdeas] = useState<any[]>([]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState<'kanban' | 'list' | 'timeline' | 'gantt'>('kanban');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { activeWorkspace } = useAuth();

  const mapIdeaStatusToColumn = (status?: string) => {
    const normalized = String(status || '').toUpperCase();
    const map: Record<string, string> = {
      CAPTURED: 'capturadas',
      ANALYSIS: 'avaliacao',
      REFINEMENT: 'pesquisa',
      BUSINESS_CASE: 'mvp',
      APPROVED: 'aprovadas',
      DEVELOPMENT: 'desenvolvimento',
      DELIVERED: 'entregues',
      ARCHIVED: 'arquivadas',
    };
    return map[normalized] || 'capturadas';
  };

  const mapIdeaPriorityToLabel = (priority?: string) => {
    const normalized = String(priority || '').toUpperCase();
    const map: Record<string, string> = {
      LOW: 'Baixa',
      MEDIUM: 'Média',
      HIGH: 'Alta',
      CRITICAL: 'Crítica',
    };
    return map[normalized] || 'Média';
  };

  const mapColumnToIdeaStatus = (column?: string) => {
    const normalized = String(column || '').toLowerCase();
    const map: Record<string, string> = {
      capturadas: 'CAPTURED',
      avaliacao: 'ANALYSIS',
      pesquisa: 'REFINEMENT',
      mvp: 'BUSINESS_CASE',
      aprovadas: 'APPROVED',
      desenvolvimento: 'DEVELOPMENT',
      entregues: 'DELIVERED',
      arquivadas: 'ARCHIVED',
    };
    return map[normalized] || 'CAPTURED';
  };

  useEffect(() => {
    if (globalFilters.ideaId && ideas && ideas.length > 0) {
      setCurrentView('ideia-workspace');
    }
  }, [globalFilters.ideaId, ideas, setCurrentView]);

  useEffect(() => {
    if (!globalFilters.ideaId) {
      fetchIdeas();
    }
  }, [globalFilters.ideaId]);

  const handleUpdateIdeaDates = async (ideaId: number, newStartDate: string, newEndDate: string) => {
    const idea = ideas.find(i => i.id === ideaId);
    if (!idea) return;

    const cleanTags = (idea.tags || []).filter((t: string) => !t.startsWith('start:') && !t.startsWith('end:'));
    const updatedTags = [...cleanTags, `start:${newStartDate}`, `end:${newEndDate}`];

    try {
      await apiClient.put(`/api/ideas/${ideaId}`, { 
          tags: updatedTags
        });

      setIdeas(prev => prev.map(i => i.id === ideaId ? { ...i, tags: updatedTags } : i));
    } catch (e) {
      console.error("Error updating idea dates in timeline:", e);
    }
  };

  const fetchIdeas = async () => {
    try {
      const { data } = await apiClient.get('/api/ideas');
      if (!data) {
        setIdeas([]);
        return;
      }

      const unwrapIdeasList = (payload: any): any[] => {
        if (Array.isArray(payload)) return payload;
        if (payload && typeof payload === 'object') {
          if (Array.isArray(payload.data)) return payload.data;
          if (Array.isArray(payload.ideas)) return payload.ideas;
          if (Array.isArray(payload.items)) return payload.items;
        }
        return [];
      };

      const dataRaw = unwrapIdeasList(data);

      const mappedData = dataRaw.map((i: any) => {
        const quarterTag = Array.isArray(i.tags)
          ? i.tags.find((t: string) => ['Q3 2026', 'Q4 2026', 'Q1 2027', 'Q2 2027'].includes(t))
          : null;

        return {
          ...i,
          name: i.title,
          categoria: i.description || 'SaaS',
          potencial: i.potencial || '$$$',
          complexidade: i.complexity || 'Média',
          score: typeof i.score === 'number' ? i.score : 80,
          empresa: i.company || '-',
          column: mapIdeaStatusToColumn(i.status),
          prioridade: mapIdeaPriorityToLabel(i.priority),
          emoji: ['💡', '🚀', '🧠', '✨', '🔥'][Math.floor(Math.random() * 5)],
          quarter: quarterTag || 'Backlog'
        };
      });

      setIdeas(mappedData);
    } catch (err) {
      console.error('Erro ao carregar ideias da API:', err);
      setIdeas([]);
    }
  };

  useEffect(() => {
    fetchIdeas();
  }, []);

  const handleUpdateIdeaStatus = async (ideaId: string, newStatus: string) => {
    try {
      await apiClient.put(`/api/ideas/${ideaId}`, { status: mapColumnToIdeaStatus(newStatus)
      });
    } catch(err) {
      console.error(err);
    }
  };

  const handleUpdateIdea = async (updated: any) => {
    try {
      const rawTags = updated.tags && Array.isArray(updated.tags) ? [...updated.tags] : [];
      const filteredTags = rawTags.filter((t: string) => !['Q3 2026', 'Q4 2026', 'Q1 2027', 'Q2 2027'].includes(t));
      if (updated.quarter && updated.quarter !== 'Backlog') {
        filteredTags.push(updated.quarter);
      }

      await apiClient.put(`/api/ideas/${updated.id}`, {
        title: updated.name, 
        description: updated.categoria,
        status: mapColumnToIdeaStatus(updated.column),
        tags: filteredTags
      });
      fetchIdeas();
    } catch(err) {
      console.error(err);
    }
    setSelectedIdea(null);
    if (globalFilters.ideaId) setGlobalFilters({ ...globalFilters, ideaId: undefined });
  };

  const handleUpdateIdeaQuarter = async (ideaId: string, newQuarter: string) => {
    try {
      const ideaToUpdate = ideas.find(i => i.id === ideaId);
      if (!ideaToUpdate) return;

      const rawTags = ideaToUpdate.tags && Array.isArray(ideaToUpdate.tags) ? [...ideaToUpdate.tags] : [];
      const filteredTags = rawTags.filter((t: string) => !['Q3 2026', 'Q4 2026', 'Q1 2027', 'Q2 2027'].includes(t));
      if (newQuarter && newQuarter !== 'Backlog') {
        filteredTags.push(newQuarter);
      }

      // Optimistic Update
      setIdeas(prev => prev.map(i => i.id === ideaId ? { ...i, quarter: newQuarter, tags: filteredTags } : i));

      await apiClient.put(`/api/ideas/${ideaId}`, { 
          tags: filteredTags
        });
    } catch(err) {
      console.error(err);
      fetchIdeas(); // Revert to database state if failed
    }
  };

  const filteredIdeas = useMemo(() => ideas.filter(i => {
    const matchesSearch = (i.name || i.title || '').toLowerCase().includes(searchQuery.toLowerCase());
    let matchesStatus = true;
    if (statusFilter !== 'ALL') {
      const normalizedStatus = statusFilter.toLowerCase();
      if (i.column !== normalizedStatus && i.status !== normalizedStatus) matchesStatus = false;
    }
    return matchesSearch && matchesStatus;
  }), [ideas, searchQuery, statusFilter]);

  const timelineItems = useMemo(() => {
    return filteredIdeas.map(idea => {
      let startDate = '';
      let endDate = '';
      
      const startTag = idea.tags?.find((t: string) => t.startsWith('start:'));
      const endTag = idea.tags?.find((t: string) => t.startsWith('end:'));
      
      if (startTag) startDate = startTag.replace('start:', '');
      if (endTag) endDate = endTag.replace('end:', '');
      
      if (!startDate) {
        if (idea.quarter === 'Q3 2026') {
          startDate = '2026-07-01';
          endDate = '2026-09-30';
        } else if (idea.quarter === 'Q4 2026') {
          startDate = '2026-10-01';
          endDate = '2026-12-31';
        } else if (idea.quarter === 'Q1 2027') {
          startDate = '2027-01-01';
          endDate = '2027-03-31';
        } else if (idea.quarter === 'Q2 2027') {
          startDate = '2027-04-01';
          endDate = '2027-06-30';
        } else {
          const today = new Date();
          startDate = today.toISOString().split('T')[0];
          const nextWeek = new Date();
          nextWeek.setDate(today.getDate() + 7);
          endDate = nextWeek.toISOString().split('T')[0];
        }
      }
      
      if (!endDate) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + 30); // 1 month default duration
        endDate = d.toISOString().split('T')[0];
      }
      
      const colLabelMap: { [key: string]: string } = {
        'capturadas': '💡 Capturadas',
        'avaliacao': '🔍 Em análise',
        'pesquisa': '📋 Refinamento',
        'mvp': '💰 Business Case',
        'aprovadas': '✅ Aprovadas',
        'desenvolvimento': '🚀 Em Desenvolvimento',
        'entregues': '🎉 Entregues',
        'arquivadas': '📦 Arquivadas'
      };

      return {
        id: idea.id,
        name: idea.name,
        startDate,
        endDate,
        status: idea.column,
        statusLabel: colLabelMap[idea.column] || idea.column,
        priority: idea.prioridade || 'Média',
        assignee: idea.empresa || '-',
        progress: idea.score || 50,
        dependencies: [],
        rawItem: idea
      } as TimelineItem;
    });
  }, [filteredIdeas]);

  const kanbanItems: KanbanItem[] = useMemo(() => {
    return filteredIdeas.map(i => ({
      id: i.id,
      title: i.name || i.title,
      subtitle: i.categoria || 'Categoria',
      owner: i.empresa || '-',
      priority: i.prioridade || 'Média',
      progress: i.score || 0,
      budgetOrValue: i.potencial || '$$$',
      budgetLabel: 'Potencial',
      status: i.column,
      raw: i
    }));
  }, [filteredIdeas]);

  const handleDropKanban = async (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    const ideaIdStr = e.dataTransfer.getData('itemId') || e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('projectId');
    let ideaId = (window as any).__draggedItemId;

    if (!ideaId && ideaIdStr) {
      ideaId = Number(ideaIdStr);
    }

    if (!ideaId) return;

    // Clear global state
    (window as any).__draggedItemId = null;

    // Optimistic Update
    setIdeas(prev => prev.map(i => i.id === Number(ideaId) ? { ...i, column: colId } : i));

    try {
      await apiClient.put(`/api/ideas/${ideaId}`, { status: mapColumnToIdeaStatus(colId)
      });
      await fetchIdeas();
    } catch(err) {
      console.error(err);
      fetchIdeas();
    }
  };

  const emAvaliacao = ideas.filter(i => i.column === 'avaliacao' || i.status === 'avaliacao').length;
  const emPesquisa = ideas.filter(i => i.column === 'pesquisa' || i.status === 'pesquisa').length;
  const mvp = ideas.filter(i => i.column === 'mvp' || i.status === 'mvp').length;
  const lancadas = ideas.filter(i => i.column === 'lancadas' || i.status === 'lancadas' || i.column === 'desenvolvimento' || i.status === 'desenvolvimento').length;
  const arquivadas = ideas.filter(i => i.column === 'arquivadas' || i.status === 'arquivadas').length;

  const toggleSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (false) {
    return <SkeletonKanban />;
  }

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-10 animate-in fade-in duration-500 relative px-4 sm:px-6 lg:px-10">
      <StandardHeader 
        title="Banco de Ideias"
        subtitle="Capture, valide e transforme ideias em produtos do ecossistema Cyzor."
        actions={[
          {
            label: 'Importar',
            icon: Upload,
            onClick: () => {},
            variant: 'secondary'
          },
          {
            label: 'Exportar',
            icon: Download,
            onClick: () => {},
            variant: 'secondary'
          },
          {
            label: 'Nova Ideia',
            icon: Plus,
            onClick: () => setIsNewModalOpen(true),
            variant: 'primary'
          }
        ]}
      >
        <AIActionDropdown entityId="ideas" actions={['evaluateIdea']} />
      </StandardHeader>
      
      <IdeaStats 
        totalIdeas={ideas.length}
        emAvaliacao={emAvaliacao}
        emPesquisa={emPesquisa}
        mvp={mvp}
        lancadas={lancadas}
        arquivadas={arquivadas}
      />

      <main className="w-full flex flex-col gap-5">
        <BoardToolbar 
          innerSearch={searchQuery}
          setInnerSearch={setSearchQuery}
          viewMode={viewMode}
          setViewMode={setViewMode}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />
        
        <div className="w-full overflow-hidden">
          {viewMode === 'kanban' && (
            <BoardKanban 
              columns={IDEIA_COLUMNS}
              items={kanbanItems}
              onDrop={handleDropKanban}
              onItemClick={(item) => {
                setGlobalFilters({ ...globalFilters, ideaId: item.id });
                setCurrentView('ideia-workspace');
              }}
              onAddClick={() => setIsNewModalOpen(true)}
              emptyMessage="Nenhuma ideia nesta etapa."
            />
          )}

          {viewMode === 'list' && (
            <BoardList 
              columns={[
                { key: 'title', label: 'Ideia' },
                { key: 'priority', label: 'Prioridade' },
                { key: 'status', label: 'Status' },
                { key: 'score', label: 'Score' }
              ]}
              items={filteredIdeas}
              onItemClick={(item) => {
                setGlobalFilters({ ...globalFilters, ideaId: item.id });
                setCurrentView('ideia-workspace');
              }}
              renderCell={(item, colKey) => {
                if (colKey === 'title') return <div className="font-bold text-neutral-900">{item.name}</div>;
                if (colKey === 'priority') return (
                  <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${
                    item.prioridade === 'Alta' ? 'bg-red-50 text-red-700 border border-red-100' :
                    item.prioridade === 'Média' ? 'bg-amber-50 text-amber-700 border border-amber-100' :
                    'bg-slate-50 text-slate-700 border border-slate-100'
                  }`}>
                    {item.prioridade}
                  </span>
                );
                if (colKey === 'status') {
                  const col = IDEIA_COLUMNS.find(c => c.id === item.column);
                  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${col?.badge || ''}`}>{col?.label || item.column}</span>;
                }
                if (colKey === 'score') return <span className="text-slate-500">{item.score}%</span>;
                return null;
              }}
            />
          )}

          {(viewMode === 'timeline' || viewMode === 'gantt') && (
            <div className="w-full bg-white p-6 rounded-[24px] border border-[#0F172A08] shadow-sm">
              <TimelineView 
                items={timelineItems}
                onUpdateItemDates={handleUpdateIdeaDates}
                onItemClick={(rawItem) => {
                  setGlobalFilters({ ...globalFilters, ideaId: rawItem.id });
                  setCurrentView('ideia-workspace');
                }}
                onDeleteItem={(ideaId) => {
                  setIdeas(prev => prev.filter(i => i.id !== ideaId));
                }}
                title="Cronograma do Banco de Ideias"
                emptyMessage="Nenhuma ideia capturada para exibir na linha do tempo."
              />
            </div>
          )}
        </div>
      </main>

      <ProductActionBar selectedCount={selectedIds.length} onClear={() => setSelectedIds([])} />

      <NewIdeaModal 
        isOpen={isNewModalOpen} 
        onClose={() => setIsNewModalOpen(false)} 
        onSuccess={fetchIdeas}
      />
    </div>
  );
}
